import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Home, DollarSign, Bed, Bath, Calendar, MapPin, ArrowLeft, Plus, Clock, User, CheckCircle, XCircle, AlertCircle, Search, Edit, Upload, Loader2, Trash2, ExternalLink, Paperclip } from 'lucide-react';
import { formatDateLocal } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetail = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const { token } = useAuth();
  const [property, setProperty] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [propertyFiles, setPropertyFiles] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [assignmentForm, setAssignmentForm] = useState({
    date: '',
    time: '13:00',
    end_time: '16:00',
    assigned_agent_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [propertyId]);

  const fetchData = async () => {
    try {
      const [propertyRes, assignmentsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/broker/properties/${propertyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setProperty(propertyRes.data);
      // Filter assignments for this property
      const filtered = assignmentsRes.data.filter(a => a.property_id === propertyId);
      setAssignments(filtered);
      setAgents(agentsRes.data.filter(a => a.status === 'approved' || a.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyFiles = async () => {
    try {
      const res = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { property_id: propertyId }
      });
      setPropertyFiles(res.data);
    } catch (err) {
      console.error('Failed to fetch files');
    }
  };

  useEffect(() => {
    if (propertyId) fetchPropertyFiles();
  }, [propertyId]);

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('property_id', propertyId);
        await axios.post(`${API}/files/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (err) {
        toast.error(`Failed: ${file.name} — ${err.response?.data?.detail || 'Upload error'}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded`);
      fetchPropertyFiles();
    }
    setUploading(false);
    setShowUploadDialog(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchPropertyFiles();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const openAssignModal = () => {
    setAssignmentForm({
      date: '',
      time: '13:00',
      end_time: '16:00',
      assigned_agent_id: '',
      description: property ? `Open house for ${property.address}` : ''
    });
    setShowAssignModal(true);
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/broker/properties/${propertyId}/assignments`,
        assignmentForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Open house assignment created! Agent has been notified.');
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 409) {
        // Handle double-booking conflict
        toast.error(error.response?.data?.detail || 'This agent is already booked for another open house at that time.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create assignment');
      }
    }
  };

  const openReassignModal = (assignment) => {
    setSelectedAssignment(assignment);
    setSearchQuery('');
    setShowReassignModal(true);
  };

  const handleReassign = async (newAgentId) => {
    if (!selectedAssignment || !newAgentId) return;
    
    try {
      await axios.put(
        `${API}/broker/assignments/${selectedAssignment.id}/reassign`,
        { new_agent_id: newAgentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assignment reassigned successfully!');
      setShowReassignModal(false);
      setSelectedAssignment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign');
    }
  };

  const openEditModal = (assignment) => {
    setSelectedAssignment(assignment);
    setAssignmentForm({
      date: assignment.date,
      time: assignment.time,
      end_time: assignment.end_time,
      assigned_agent_id: assignment.assigned_agent_id,
      description: assignment.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) return;

    try {
      await axios.put(
        `${API}/broker/assignments/${selectedAssignment.id}`,
        assignmentForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assignment updated successfully!');
      setShowEditModal(false);
      setSelectedAssignment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    }
  };

  // Filter agents based on search query
  const filteredAgents = agents.filter(agent => 
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-stone-600">Loading property details...</p>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-stone-600">Property not found</p>
          <Button onClick={() => navigate('/broker/properties')} className="mt-4">
            Back to Properties
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/broker/properties')}
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Properties
          </Button>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Property Details
          </h1>
        </div>

        {/* Property Info Card */}
        <Card className="p-4 sm:p-8 bg-white rounded-2xl card-shadow mb-8">
          <div className="flex items-start gap-3 sm:gap-4 mb-6">
            <div className="p-3 sm:p-4 rounded-full bg-[#1A4D2E]/10 shrink-0">
              <Home className="text-[#1A4D2E]" size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-3 break-words" style={{fontFamily: '"Playfair Display", serif'}}>
                <MapPin className="inline mr-1 sm:mr-2 shrink-0" size={20} />
                {property.address}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
                <div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-1">Price</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-700">
                    <DollarSign className="inline" size={16} />
                    {new Intl.NumberFormat('en-US').format(property.price)}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-1">Bedrooms</p>
                  <p className="text-base sm:text-xl font-semibold">
                    <Bed className="inline mr-1" size={16} />
                    {property.bedrooms}
                  </p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-1">Bathrooms</p>
                  <p className="text-base sm:text-xl font-semibold">
                    <Bath className="inline mr-1" size={16} />
                    {property.bathrooms}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-stone-500 mb-1">MLS#</p>
                  <p className="text-base sm:text-xl font-semibold truncate">{property.mls_number}</p>
                </div>
              </div>
            </div>
          </div>

          {property.property_type && (
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                {property.property_type}
              </span>
            </div>
          )}

          {/* Property Files Section */}
          <div className="pt-4 border-t border-stone-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 flex items-center gap-2">
                <Paperclip size={14} />
                Attachments
              </h3>
              <Button
                size="sm"
                onClick={() => setShowUploadDialog(true)}
                className="bg-[#1A4D2E] text-white hover:bg-[#143D24] rounded-full h-8 px-4 text-xs"
                data-testid="property-upload-btn"
              >
                <Upload size={14} className="mr-1" />
                Upload File
              </Button>
            </div>
            {propertyFiles.length > 0 ? (
              <div className="space-y-2 mb-4">
                {propertyFiles.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg border border-stone-200 group" data-testid={`file-item-${f.id}`}>
                    <a
                      href={f.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#1A4D2E] hover:underline font-medium truncate flex-1 mr-3"
                      data-testid={`file-link-${f.id}`}
                    >
                      <ExternalLink size={14} className="flex-shrink-0" />
                      {f.original_filename}
                    </a>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteFile(f.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`file-delete-${f.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-400 text-sm mb-4">No files attached to this property.</p>
            )}
          </div>

          {property.description && (
            <div className="pt-4 border-t border-stone-200">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">Description</h3>
              <p className={`text-stone-700 text-lg leading-relaxed ${!isDescriptionExpanded ? 'line-clamp-3' : ''}`}>
                {property.description}
              </p>
              {property.description.length > 200 && (
                <button
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="text-[#1A4D2E] hover:underline text-sm mt-2 font-medium"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
        </Card>

        {/* Assignments Section */}
        <Card className="p-4 sm:p-8 bg-white rounded-2xl card-shadow">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              Open House Assignments ({assignments.length})
            </h2>
            <Button
              onClick={openAssignModal}
              className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24] w-full sm:w-auto"
              disabled={agents.length === 0}
            >
              <Plus size={20} className="mr-2" />
              New Assignment
            </Button>
          </div>

          {assignments.length === 0 ? (
            <div className="text-center py-12 bg-stone-50 rounded-lg">
              <Calendar className="mx-auto mb-4 text-stone-300" size={64} />
              <h3 className="text-xl font-bold text-stone-700 mb-2">No Open House Assignments Yet</h3>
              <p className="text-stone-600 mb-6">Click "New Assignment" to schedule an open house</p>
              {agents.length === 0 && (
                <p className="text-orange-600 text-sm">⚠️ No active agents available. Add agents first.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {assignments.map((assignment) => (
                <Card key={assignment.id} className={`p-6 ${
                  assignment.status === 'accepted' ? 'border-2 border-green-200 bg-green-50' :
                  assignment.status === 'rejected' ? 'border border-red-200 bg-red-50 opacity-60' :
                  'border-2 border-orange-200 bg-orange-50'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          assignment.status === 'accepted' ? 'bg-green-200 text-green-800' :
                          assignment.status === 'rejected' ? 'bg-red-200 text-red-800' :
                          'bg-orange-200 text-orange-800'
                        }`}>
                          {assignment.status === 'accepted' && <CheckCircle className="inline mr-1" size={14} />}
                          {assignment.status === 'rejected' && <XCircle className="inline mr-1" size={14} />}
                          {assignment.status === 'pending' && <AlertCircle className="inline mr-1" size={14} />}
                          {assignment.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="font-semibold text-[#1A4D2E] mb-2">
                            <User className="inline mr-2" size={16} />
                            {assignment.assigned_agent_name}
                          </p>
                          <p className="text-stone-600 text-sm">
                            <Calendar className="inline mr-2" size={14} />
                            {formatDateLocal(assignment.date)}
                          </p>
                          <p className="text-stone-600 text-sm">
                            <Clock className="inline mr-2" size={14} />
                            {assignment.time}{assignment.end_time ? ` - ${assignment.end_time}` : ''}
                          </p>
                        </div>
                        <div>
                          {assignment.description && (
                            <p className="text-stone-600 text-sm mb-2">
                              <strong>Note:</strong> {assignment.description}
                            </p>
                          )}
                          {assignment.agent_note && (
                            <p className="text-green-700 text-sm italic">
                              <strong>Agent:</strong> "{assignment.agent_note}"
                            </p>
                          )}
                          {assignment.event_id && (
                            <p className="text-blue-600 text-sm font-semibold mt-2">
                              ✓ Open house event created
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {(() => {
                        const eventDate = assignment.date ? new Date(assignment.date + 'T23:59:59') : null;
                        const oneDayAfter = eventDate ? new Date(eventDate.getTime() + 24 * 60 * 60 * 1000) : null;
                        const isEditable = !oneDayAfter || new Date() <= oneDayAfter;
                        return (
                          <>
                            {isEditable && assignment.status !== 'accepted' && assignment.status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(assignment)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit size={16} />
                              </Button>
                            )}
                            {assignment.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReassignModal(assignment)}
                              >
                                Reassign
                              </Button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Assignment Modal */}
        <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule Open House</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAssignment} className="space-y-4 mt-4">
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-sm font-semibold text-[#1A4D2E]">{property.address}</p>
                <p className="text-xs text-stone-600">
                  {property.bedrooms} bed • {property.bathrooms} bath • ${new Intl.NumberFormat('en-US').format(property.price)}
                </p>
              </div>

              <div>
                <Label htmlFor="assign_date">Date *</Label>
                <Input
                  id="assign_date"
                  type="date"
                  required
                  value={assignmentForm.date}
                  onChange={(e) => setAssignmentForm({...assignmentForm, date: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assign_time">Start Time *</Label>
                  <Input
                    id="assign_time"
                    type="time"
                    required
                    value={assignmentForm.time}
                    onChange={(e) => setAssignmentForm({...assignmentForm, time: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="assign_end_time">End Time *</Label>
                  <Input
                    id="assign_end_time"
                    type="time"
                    required
                    value={assignmentForm.end_time}
                    onChange={(e) => setAssignmentForm({...assignmentForm, end_time: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="assign_agent">Assign to Agent *</Label>
                <Select
                  required
                  value={assignmentForm.assigned_agent_id}
                  onValueChange={(value) => setAssignmentForm({...assignmentForm, assigned_agent_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((ag) => (
                      <SelectItem key={ag.id} value={ag.id}>
                        {ag.name} ({ag.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="assign_description">Notes</Label>
                <Textarea
                  id="assign_description"
                  value={assignmentForm.description}
                  onChange={(e) => setAssignmentForm({...assignmentForm, description: e.target.value})}
                  placeholder="Additional notes for the agent..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1A4D2E] text-white hover:bg-[#143D24]">
                  Create Assignment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reassign Modal */}
        <Dialog open={showReassignModal} onOpenChange={setShowReassignModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Reassign Open House</DialogTitle>
            </DialogHeader>
            {selectedAssignment && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm font-semibold text-[#1A4D2E] mb-1">{selectedAssignment.property_address}</p>
                  <p className="text-xs text-stone-600">
                    {formatDateLocal(selectedAssignment.date)} • {selectedAssignment.time}{selectedAssignment.end_time ? ` - ${selectedAssignment.end_time}` : ''}
                  </p>
                  <p className="text-xs text-stone-500 mt-2">
                    Currently assigned to: <span className="font-semibold">{selectedAssignment.assigned_agent_name}</span>
                  </p>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={20} />
                  <Input
                    placeholder="Search agents by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Agent List */}
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  {filteredAgents.length === 0 ? (
                    <div className="p-8 text-center text-stone-500">
                      <User className="mx-auto mb-2 text-stone-300" size={48} />
                      {searchQuery ? 'No agents found matching your search' : 'No active agents available'}
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredAgents.map((agent) => (
                        <button
                          key={agent.id}
                          onClick={() => handleReassign(agent.id)}
                          disabled={agent.id === selectedAssignment.assigned_agent_id}
                          className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                            agent.id === selectedAssignment.assigned_agent_id 
                              ? 'opacity-50 cursor-not-allowed bg-stone-100' 
                              : 'cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-[#1A4D2E]">
                                <User className="inline mr-2" size={16} />
                                {agent.name}
                              </p>
                              <p className="text-sm text-stone-600">{agent.email}</p>
                              {agent.phone && (
                                <p className="text-xs text-stone-500 mt-1">{agent.phone}</p>
                              )}
                            </div>
                            {agent.id === selectedAssignment.assigned_agent_id && (
                              <span className="text-xs bg-stone-200 text-stone-700 px-2 py-1 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowReassignModal(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Assignment Dialog */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Open House Assignment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateAssignment} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-date">Date *</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    required
                    value={assignmentForm.date}
                    onChange={(e) => setAssignmentForm({...assignmentForm, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-agent">Assigned Agent *</Label>
                  <Select
                    value={assignmentForm.assigned_agent_id}
                    onValueChange={(value) => setAssignmentForm({...assignmentForm, assigned_agent_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-time">Start Time *</Label>
                  <Input
                    id="edit-time"
                    type="time"
                    required
                    value={assignmentForm.time}
                    onChange={(e) => setAssignmentForm({...assignmentForm, time: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-end-time">End Time *</Label>
                  <Input
                    id="edit-end-time"
                    type="time"
                    required
                    value={assignmentForm.end_time}
                    onChange={(e) => setAssignmentForm({...assignmentForm, end_time: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-description">Description / Notes</Label>
                <Textarea
                  id="edit-description"
                  value={assignmentForm.description}
                  onChange={(e) => setAssignmentForm({...assignmentForm, description: e.target.value})}
                  placeholder="Any special instructions..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1A4D2E] text-white hover:bg-[#143D24]">
                  Update Assignment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* File Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Files</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-stone-600">
                Select files to attach to this property.
              </p>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="cursor-pointer"
                data-testid="file-upload-input"
              />
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Loader2 size={16} className="animate-spin" />
                  Uploading...
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PropertyDetail;
